/*
if prop is mandatory on GQL API, can be undefined (since client query may not include it)
if prop is optional on GQL API, can be undefined or null (null if requested and not set in DB)

type MyType = {
    mandatoryProp?: string;
    optionalProp?: string | null;
}
*/

// GraphQL API types

import { ApolloError } from 'apollo-client';

export type Airing = {
	channelId: string;
	id: string;
	startTime: string;
	businessLogic: string;
	channel?: Channel | null;
	editorialHighlight?: EditorialHighlight | null;
	game?: Game | null;
	status: Status;
	highlights?: HighlightConnection;
	offsets?: OffsetConnection | null;
	clientExclusions?: AiringClientExclusions | null;
	alternateIds?: AlternateIdConnection;
	exclusionPlayIds?: string[];
	programSynopsis?: string;
	programStartTime?: string;
	programEndTime?: string;
	drm?: boolean;
	mAltId?: string;
	sources?: SimpleAsset[];
	mafStage?: string;
};

export enum Status {
	SCHEDULED_WITH_MAF = 'SCHEDULED_WITH_MAF',
	LIVE = 'LIVE',
	COMPLETED = 'COMPLETED'
}

export enum AlternateIdNamespaces {
	MERLIN_ALTERNATE_ID = 'MERLIN_ALTERNATE_ID',
	MERLIN_ID = 'MERLIN_ID',
	MERLIN_LISTING_ID = 'MERLIN_LISTING_ID',
	MERLIN_PROGRAM_ID = 'MERLIN_PROGRAM_ID',
	PID = 'PID',
	PROTECTED_AIRING = 'PROTECTED_AIRING',
	SKIP_PROGRAM_ID = 'SKIP_PROGRAM_ID'
}

export type AlternateId = {
	id: string;
	alternateId: string;
	namespace: AlternateIdNamespaces;
};

export type AlternateIdInput = {
	id: string;
	alternateId: string;
	namespace: AlternateIdNamespaces;
};

export type AlternateIdConnection = {
	items: [AlternateId];
	nextToken: string;
};

export type CreateOrUpdateAlternateIdVariables = {
	alternateId: AlternateId;
};

export type CreateOrUpdateAlternateIdResult = {
	createOrUpdateAlternateId: AlternateId;
};

export type DeleteAlternateIdVariables = {
	id: string;
};

export type DeleteAlternateIdResult = {
	deleteAlternateId: {
		id: string;
		gameId: string;
	};
};

export type HighlightConnection = {
	items: Highlight[];
};

export type EditorialHighlight = {
	id?: string;
	airingId?: string | null;
	gameId?: string | null;
	periods: PeriodConnection;
};

export type EditorialHighlightInput = {
	id?: string;
	airingId: string;
	gameId: string;
	periods: PeriodInputConnection;
};

export type PeriodInputConnection = {
	items: PeriodInput[];
};

type PeriodConnection = {
	items: Period[] | null;
};

export type Channel = {
	id: string;
	sourceAssets?: VideoAssetConnection | null;
	name?: string | null;
	language?: Language | null;
	territory?: Territory | null;
	type?: ChannelType | null;
	highlights?: boolean | null;
	mediaLiveId?: string | null;
	mediaPackageId?: string | null;
	__typename?: 'Channel';
};

export type ChannelInput = {
	id?: string;
	name?: string | null;
	sourceAssets?: VideoAssetConnection | null;
	language?: Language | null;
	territory?: Territory | null;
	type?: ChannelType | null;
	highlights?: boolean | null;
};

export type VideoAssetConnection = {
	items: VideoAsset[] | null;
};

export enum ChannelType {
	LIVE,
	TEST,
	EXTERNAL
}

export enum Territory {
	GB,
	DE,
	IT,
	FR,
	AT,
	US
}

export enum Language {
	en,
	de,
	it,
	fr,
	es
}

export type AiringInput = {
	id?: string;
	channelId: string;
	startTime: string;
	businessLogic: string;
	status: Status;
	offsets?: OffsetInputConnection | null;
	clientExclusions?: string | null;
	exclusionPlayIds?: string[];
	programSynopsis?: string | null;
	programStartTime?: string | null;
	programEndTime?: string | null;
	drm?: boolean;
	alternateIds?: AlternateId[];
};

export type Event = {
	description?: string | null;
	gameEndTime?: GameTime | null;
	sourceUpdateTime: string;
	startGameTime: GameTime;
	startVideoTime?: string | null;
	subtype: string;
	type: string;
	endPosition?: string | null;
	startPosition?: string | null;
	__typename?: 'Event';
};

export type EventInput = {
	startGameTime: GameTimeInput;
	gameEndTime?: GameTimeInput;
	sourceUpdateTime: string;
	startVideoTime?: string;
	type: string;
	subtype: string;
	description?: string;
};

export type CricketTeamConfig = { id: string; name: string; players: string[] };

export type Game = {
	id: string;
	airings?: { items: Airing[] } | null;
	fers?: Fer[] | null;
	sport?: string | null;
	startTime?: string | null;
	teams?: { items?: Team[] } | null;
	offsets?: OffsetConnection | null;
	title?: string | null;
	league?: League;
	status?: string | null;
	highlights?: HighlightConnection | null;
	editorialHighlight?: EditorialHighlight | null;
	excludeScheduling?: boolean;
	__typename?: 'Game';
};

export type League = {
	id: string;
	name: string | null;
	sport: string | null;
	displayName: string | null;
	season?: {
		year: number;
		discipline: string;
		gender: string;
	};
};

export type GameInput = {
	id: string;
	sport?: string;
	startTime?: string;
	title?: string;
	offsets?: OffsetInputConnection;
	excludeScheduling?: boolean;
};

export type GameTime = {
	position: string;
	segment: number;
	segmentId?: string | null;
	__typename?: 'GameTime';
};

export type GameTimeInput = {
	position: string;
	segment: number;
	segmentId?: string;
};

export type Highlight = {
	id: string;
	available?: boolean | null;
	availableTime?: string | null;
	gameId?: string | null;
	airingId?: string | null;
	game?: Game | null;
	periods?: { items?: Period[] } | null;
	publishTime?: string | null;
	title?: string | null;
	type?: string | null;
	videoAssets?: { items: VideoAsset[] } | null;
	channelId?: string | null;
	drm?: boolean;
	sources?: SimpleAsset[];
	__typename?: 'Highlight';
};

export type ExclusionList = {
	id?: string;
	airingId?: string;
	gameId?: string;
	periods?: ExclusionPeriodConnection;
};

export type ExclusionPeriodConnection = {
	items: ExclusionPeriod[] | null;
};

export type ExclusionPeriod = {
	id: string;
	playId: string;
	title: string;
	shortText?: string;
	startTime: string;
	endTime: string;
	endpoint?: string;
};

export type ExclusionListInput = {
	id?: string;
	airingId?: string;
	gameId?: string;
	periods: ExclusionPeriodConnection;
};

export type StringFilterInput = {
	ne?: string;
	eq?: string;
	le?: string;
	lt?: string;
	ge?: string;
	gt?: string;
	contains?: string;
	notContains?: string;
	between?: string[];
	beginsWith?: string;
};

export type HighlightFilterInput = {
	type: StringFilterInput;

	and?: [HighlightFilterInput];
	or?: [HighlightFilterInput];
	not?: HighlightFilterInput;
};

export type HighlightInput = {
	id: string;
	gameId?: string;
	periods?: { items: Period[] };
	title?: string;
	type?: string;
};

export type PaginationOptions = {
	error?: ApolloError | null;
	pageSize: number;
	pageOptions: number[];
	page: number;
	loading?: boolean | false;
	handlePageSizeChange: (n: number) => void;
	handlePageChange: (n: number) => void;
	handleFilterChange?: (s: string) => void;
};

export type Period = {
	description?: string | null;
	endPosition?: string | null;
	endScore?: string | null;
	gameEndTime?: GameTime | null;
	shortText?: string | null;
	source: Source;
	startGameTime?: GameTime | null;
	startPosition?: string | null;
	teamId?: string | null;
	team?: Team | null;
	timelines?: { items: Timeline[] } | null;
	title?: string | null;
	type: string;
	endpoint?: string;
	endpoints?: periodEndpoint[];
	startGameTimeEditValue?: number | null;
	id?: string | null;
	playId?: string | null;
	excitementScore?: number | null;
	excitementThreshold?: number | null;
	__typename?: 'Period';
};

export type periodEndpoint = {
	url: string;
	format: string;
};

export type PeriodInput = {
	description?: string;
	endScore?: string;
	gameEndTime?: GameTime;
	shortText?: string;
	source: SourceInput;
	startGameTime?: GameTime;
	teamId?: string;
	timelines?: { items: TimelineInput[] };
	title?: string;
	type: string;
	id?: string | null;
	playId?: string | null;
};

export type Source = {
	type: string;
	channelId?: string | null;
	manifestKey?: string | null;
	startTime?: string | null;
	endTime?: string | null;
	gameId?: string | null;
	__typename?: 'Source';
};

export type SourceInput = {
	type: string;
	channelId?: string;
	manifestKey?: string;
	startTime?: string;
	endTime?: string;
	gameId?: string;
};

export type Team = {
	id: string;
	abbreviation?: string | null;
	name?: string | null;
	logoUrl?: string | null;
	merlinId?: string | null;
	__typename?: 'Team';
};

export type TeamInput = {
	id: string;
};

export type Timeline = {
	type: string;
	events?: { items: Event[] } | null;
	__typename?: 'Timeline';
};

export type TimelineInput = {
	type: string;
	events?: { items: EventInput[] };
};

export type VideoAsset = {
	id: string;
	format: string;
	url: string;
	cdnIdentifierArn: string;
	__typename?: 'VideoAsset';
};

export type VideoAssetInput = {
	id: string;
	format: string;
	url: string;
	cdnIdentifierArn: string;
};

export type OffsetConnection = {
	items: Offset[] | null;
};

export type Offset = {
	segment: number;
	offset: number;
	__typename?: 'Offset';
};

export type OffsetInput = {
	segment: number;
	offset: number;
};

export type OffsetInputConnection = {
	items: OffsetInput[];
};

// Query and mutation variables and results

export type CreateOrUpdateAiringVariables = {
	airing: AiringInput;
	gameId: string;
	mafStage?: string;
};

export type CreateAiringResult = {
	createAiring: Airing;
};

export type UpdateAiringResult = {
	updateAiring: Airing;
};

export type DeleteAiringVariables = {
	id: string;
};

export type DeleteAiringResult = {
	deleteAiring: {
		id: string;
		gameId: string;
	};
};

export enum HighlightType {
	DRAFT = 'DRAFT',
	LIVE = 'LIVE',
	AUTOMATED = 'AUTOMATED'
}

export type GetGameResult = {
	getGame: Game;
};

export type NotificationInput = {
	id: string;
	notificationType: NotificationType;
	timestamp: string;
	title: string;
	minute: number | undefined;
	teamId: string | undefined;
	score: string | undefined;
};

export enum NotificationType {
	VIDEO = 'VIDEO',
	TEXT = 'TEXT'
}

export enum Clients {
	LIGHTNING = 'LIGHTNING',
	MATCH_ALERTS = 'MATCH_ALERTS',
	PEACOCK = 'PEACOCK',
	SKY_GO = 'SKY_GO'
}

export type AiringClientExclusions = Clients[];

export type LoadingOptions = {
	error?: ApolloError | null;
	loading?: boolean | false;
	handleReload: () => void;
};

export type Fer = {
	__typename: string;
	available: string;
	channel?: Channel | null;
	channelId: string;
	endpoint?: string;
	endTime?: string;
	entity?: string;
	ferId: string;
	gameId: string;
	startTime: string;
};

export type FerInput = {
	channelId: string;
	startTime: string;
	gameId: string;
};

export type CreateOrUpdateFerResult = {
	createOrUpdateFer: Fer;
};

export type CreateFerVariables = {
	fer: FerInput;
};

export type UpdateFerVariables = {
	ferId: string;
	fer: FerInput;
};

export type DeleteFerResult = {
	deleteFer: {
		gameId: string;
		ferId: string;
	};
};

export type DeleteFerVariables = {
	gameId: string;
	ferId: string;
};

export type SendFerToHarvestResult = {
	ferSendToHarvesting: {
		ferId: string;
	};
};

export type SendFerToHarvestVariables = {
	ferId: string;
	endTime: string;
};

export type DailyTestGame = {
	id: string;
	gameObject: {
		league: {
			id: string;
		};
		id: string;
		sport: string;
		startTime: string;
		title: string;
	};
	airing: {
		channelId: string;
		airingBusinessLogic: string | null;
	}[];
	abbrTitle?: string;
};

export interface Alignment {
	playByPlay: PbP[];
}

export interface PbP {
	__typename: string;
	id: string;
	minute: number;
	second: number;
	playType: string;
	scrubberInfo?: {
		confidence?: number;
		videoTime: number;
	};
	eventPlayer?: {
		displayName: string;
		firstName: string;
		lastName: string;
	};
	playerOne?: {
		displayName: string;
	};
	exact_video_time?: number;
	has_excitement?: boolean;
	excitement_score?: number;
}

export enum DRMTYPE {
	AES128 = 'aes128',
	CLEARKEY = 'clearkey',
	FAIRPLAY = 'fairplay',
	PLAYREADY = 'playready',
	WIDEVINE = 'widevine'
}

export interface SimpleAsset {
	url?: string;
	drmProvider: string;
	contentRef: string;
	configAlias: string;
	propositionContentId: string;
	protection: string;
	transport: string;
}

export enum USE_DRM {
	YES = 'yes',
	NO = 'no',
	BOTH = 'both'
}

export const HLS_TRANSPORT = 'HLS';
